import { Container, MeshRope, Point, Texture } from 'pixi.js';
import { AssetKeys } from './Assets';
import type { VineState } from '@benji/sim-core';

const ROPE_THICKNESS = 36;
const TEXTURE_LATERAL_OFFSET = 10;
const TEXTURE_LONGITUDINAL_OFFSET = 18;

/**
 * Render-only mesh for one authoritative {@link VineState}. The physics lives
 * in the sim's `RopeSim`; this just rebuilds the textured ribbon from the
 * simulated point positions each frame.
 */
export class VineView extends Container {
  readonly vine: VineState;
  private readonly mesh: MeshRope;
  private readonly meshPoints: Point[];

  constructor(textures: Record<string, Texture>, vine: VineState) {
    super();
    this.vine = vine;
    this.meshPoints = vine.points.map((p) => new Point(p.x, p.y));
    this.mesh = new MeshRope({
      texture: textures[AssetKeys.VineRotated],
      points: this.meshPoints,
      width: ROPE_THICKNESS,
    });
    this.addChild(this.mesh);
  }

  sync(alpha: number) {
    const points = this.vine.points;
    const prevPts = this.vine.prevPoints;
    const n = points.length;
    // Interpolate every rope point between the last two ticks.
    const ix = (i: number) => prevPts[i].x + (points[i].x - prevPts[i].x) * alpha;
    const iy = (i: number) => prevPts[i].y + (points[i].y - prevPts[i].y) * alpha;
    for (let i = 0; i < n; i++) {
      const px = ix(i);
      const py = iy(i);
      const prevI = Math.max(0, i - 1);
      const nextI = Math.min(n - 1, i + 1);
      const dx = ix(nextI) - ix(prevI);
      const dy = iy(nextI) - iy(prevI);
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const tx = dx / len;
      const ty = dy / len;
      const nx = -ty;
      const ny = tx;
      this.meshPoints[i].x = px + nx * TEXTURE_LATERAL_OFFSET + tx * TEXTURE_LONGITUDINAL_OFFSET;
      this.meshPoints[i].y = py + ny * TEXTURE_LATERAL_OFFSET + ty * TEXTURE_LONGITUDINAL_OFFSET;
    }
    this.alpha = this.vine.broken ? Math.max(0, 1 - this.vine.brokenProgress) : 1;
  }
}
